/**
 * Affiliate Bonus Item Handler
 * 
 * Adds a free bonus venison chips item to cart when user adds any product
 * on an affiliate page AND the cart total reaches $100+. The bonus item is 
 * marked with a hidden "_bonus_item" property to exempt it from the minimum 
 * quantity of 2 rule.
 */

(function() {
  'use strict';

  // Configuration
  const BONUS_PRODUCT_HANDLE = 'wild-axis-deer-venison-chips';
  const BONUS_VARIANT_ID = 43981507494003; // Hardcoded variant ID for venison chips
  const BONUS_DISCOUNT_CODES = ['LUKE']; // Discount codes that trigger the bonus (uppercase)
  const STORAGE_KEY = 'affiliate_bonus_added';
  const MINIMUM_CART_TOTAL_CENTS = 10000; // $100.00 in cents - minimum cart total required for bonus

  /**
   * Fetch the current cart
   */
  async function fetchCart() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (e) {
      console.error('[AffiliateBonusItem] Error fetching cart:', e);
      return null;
    }
  }

  /**
   * Check if a bonus item already exists in the cart
   */
  async function checkCartForBonusItem() {
    try {
      const cart = await fetchCart();
      if (!cart) return false;
      
      return cart.items.some(item => {
        if (item.properties && item.properties._bonus_item) {
          return true;
        }
        return false;
      });
    } catch (e) {
      console.error('[AffiliateBonusItem] Error checking cart for bonus:', e);
      return false;
    }
  }

  /**
   * Calculate the cart total excluding bonus items
   * @returns {number} Cart total in cents, excluding bonus items
   */
  async function getCartTotalExcludingBonus() {
    try {
      const cart = await fetchCart();
      if (!cart || !cart.items) return 0;
      
      // Sum up prices of non-bonus items
      let total = 0;
      for (const item of cart.items) {
        // Skip bonus items
        if (item.properties && item.properties._bonus_item) {
          continue;
        }
        total += item.final_line_price; // This is in cents
      }
      
      console.log('[AffiliateBonusItem] Cart total (excluding bonus):', total, 'cents ($' + (total / 100).toFixed(2) + ')');
      return total;
    } catch (e) {
      console.error('[AffiliateBonusItem] Error calculating cart total:', e);
      return 0;
    }
  }

  /**
   * Check if cart total meets the minimum threshold for the bonus
   */
  async function cartMeetsMinimum() {
    const total = await getCartTotalExcludingBonus();
    const meetsMinimum = total >= MINIMUM_CART_TOTAL_CENTS;
    console.log('[AffiliateBonusItem] Minimum threshold check:', 
      'Cart: $' + (total / 100).toFixed(2), 
      'Required: $' + (MINIMUM_CART_TOTAL_CENTS / 100).toFixed(2),
      'Meets minimum:', meetsMinimum
    );
    return meetsMinimum;
  }

  /**
   * Check if bonus was already added this session
   */
  function wasBonusAlreadyAdded() {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
        if (data.added === true && data.timestamp > oneDayAgo) {
          return true;
        }
      }
    } catch (e) {
      console.error('[AffiliateBonusItem] Error reading session storage:', e);
    }
    return false;
  }

  /**
   * Mark the bonus as added
   */
  function markBonusAsAdded() {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        added: true,
        timestamp: Date.now()
      }));
    } catch (e) {
      console.error('[AffiliateBonusItem] Error writing session storage:', e);
    }
  }

  /**
   * Add the bonus item to cart
   */
  async function addBonusItemToCart() {
    try {
      console.log('[AffiliateBonusItem] Adding bonus venison chips, variant:', BONUS_VARIANT_ID);
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: BONUS_VARIANT_ID,
          quantity: 1,
          properties: {
            '_bonus_item': 'true',
            '_discount_bonus': 'Free with qualifying purchase',
            '_min_quantity': '1',
            '_skip_min_quantity': 'true'
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to add bonus item: ${response.status}`);
      }

      const result = await response.json();
      console.log('[AffiliateBonusItem] Bonus item added successfully:', result);
      
      // Trigger cart update for UpCart
      if (typeof window.upcartOnCartLoaded === 'function') {
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        window.upcartOnCartLoaded(cart);
      }
      
      // Refresh cart drawer if available
      if (typeof window.refreshCart === 'function') {
        window.refreshCart();
      }
      
      return true;
    } catch (e) {
      console.error('[AffiliateBonusItem] Error adding bonus item:', e);
      return false;
    }
  }

  /**
   * Check if we should add the bonus item
   * Uses "one bonus per cart" logic and requires cart to meet $100 minimum
   */
  async function shouldAddBonus() {
    // Check if cart already has bonus item
    const hasBonusItem = await checkCartForBonusItem();
    if (hasBonusItem) {
      console.log('[AffiliateBonusItem] Cart already contains a bonus item');
      return false;
    }

    // Check if cart total meets the minimum threshold ($100)
    const meetsMinimum = await cartMeetsMinimum();
    if (!meetsMinimum) {
      console.log('[AffiliateBonusItem] Cart does not meet minimum threshold for bonus');
      return false;
    }

    console.log('[AffiliateBonusItem] No bonus item in cart and meets minimum, will add one');
    return true;
  }

  /**
   * Handle add to cart - intercept and add bonus item
   */
  async function handleAddToCart() {
    console.log('[AffiliateBonusItem] Add to cart detected on affiliate page');
    
    // Small delay to let the main item be added first
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Check if we should add bonus
    const shouldAdd = await shouldAddBonus();
    if (!shouldAdd) {
      return;
    }

    // Add the bonus item
    await addBonusItemToCart();
  }

  /**
   * Intercept fetch calls to /cart/add.js
   */
  function interceptFetch() {
    const originalFetch = window.fetch;
    
    window.fetch = async function(...args) {
      const result = await originalFetch.apply(this, args);
      
      // Check if this was an add to cart request
      const url = args[0];
      if (typeof url === 'string' && url.includes('/cart/add')) {
        // Don't intercept our own bonus item additions
        const body = args[1]?.body;
        if (body && typeof body === 'string' && body.includes('_bonus_item')) {
          return result;
        }
        
        // Trigger bonus item addition
        handleAddToCart();
      }
      
      return result;
    };
    
    console.log('[AffiliateBonusItem] Fetch interceptor installed');
  }

  /**
   * Listen for form submissions (traditional add to cart)
   */
  function interceptForms() {
    document.addEventListener('submit', function(e) {
      const form = e.target;
      if (form.action && form.action.includes('/cart/add')) {
        console.log('[AffiliateBonusItem] Form submission to cart detected');
        // For form submissions, we need to add bonus after page might reload
        // Store a flag and check on next page load
        sessionStorage.setItem('affiliate_bonus_pending', 'true');
      }
    });
  }

  /**
   * Check for pending bonus addition (from form submission)
   */
  async function checkPendingBonus() {
    const pending = sessionStorage.getItem('affiliate_bonus_pending');
    if (pending === 'true') {
      sessionStorage.removeItem('affiliate_bonus_pending');
      console.log('[AffiliateBonusItem] Processing pending bonus from form submission');
      
      const shouldAdd = await shouldAddBonus();
      if (shouldAdd) {
        await addBonusItemToCart();
      }
    }
  }

  /**
   * Listen for click events on add to cart buttons (backup method)
   */
  function interceptClicks() {
    document.addEventListener('click', async function(e) {
      const button = e.target.closest('button[type="submit"], .add-to-cart, [data-js-product-add-to-cart], .quick-add-to-cart button');
      
      if (button) {
        const form = button.closest('form');
        if (form && form.action && (form.action.includes('/cart/add') || form.action.includes('/cart'))) {
          console.log('[AffiliateBonusItem] Add to cart button clicked');
          
          // Wait for the add to cart to complete
          setTimeout(async () => {
            const shouldAdd = await shouldAddBonus();
            if (shouldAdd) {
              await addBonusItemToCart();
            }
          }, 1000);
        }
      }
    });
    
    console.log('[AffiliateBonusItem] Click interceptor installed');
  }

  /**
   * Initialize the script
   */
  function init() {
    const scriptElement = document.querySelector('script[data-affiliate-bonus]');
    if (!scriptElement) {
      console.log('[AffiliateBonusItem] Script element not found, not on affiliate page');
      return;
    }

    console.log('[AffiliateBonusItem] Initializing on affiliate page...');
    console.log('[AffiliateBonusItem] Bonus variant ID:', BONUS_VARIANT_ID);

    // Install interceptors
    interceptFetch();
    interceptClicks();
    interceptForms();
    
    // Check for pending bonus from previous form submission
    checkPendingBonus();
    
    console.log('[AffiliateBonusItem] Ready - bonus will be added when cart reaches $' + (MINIMUM_CART_TOTAL_CENTS / 100).toFixed(2));
  }

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for manual triggering/debugging
  window.AffiliateBonusItem = {
    addBonusItem: addBonusItemToCart,
    checkCartForBonusItem: checkCartForBonusItem,
    shouldAddBonus: shouldAddBonus,
    cartMeetsMinimum: cartMeetsMinimum,
    getCartTotal: getCartTotalExcludingBonus,
    MINIMUM_THRESHOLD: MINIMUM_CART_TOTAL_CENTS,
    clearSession: function() {
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem('affiliate_bonus_pending');
      console.log('[AffiliateBonusItem] Session cleared - bonus will be added on next add to cart');
    },
    // Manual trigger for testing
    triggerBonus: async function() {
      const shouldAdd = await shouldAddBonus();
      if (shouldAdd) {
        await addBonusItemToCart();
      } else {
        console.log('[AffiliateBonusItem] Bonus already added or in cart, or cart below $' + (MINIMUM_CART_TOTAL_CENTS / 100).toFixed(2));
      }
    }
  };

})();
